import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "../context/I18nContext";
import translations from "../i18n/translations";
import * as programTestsService from "../services/programTests";
import { clearPlacementLead, loadPlacementLead } from "./PlacementTestStart";
import SharedExerciseRunner, {
  exerciseHasAnswer,
} from "../components/tests/ExerciseRunner";
import "./PlacementTestTake.css";

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function formatClock(totalSeconds) {
  const safe = Math.max(0, Number(totalSeconds) || 0);
  const m = Math.floor(safe / 60);
  const s = safe % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

function getPlacementTimerKey(testId) {
  return `casaclass:placementTimerStart:${testId}`;
}

export default function PlacementTestTake() {
  const { testId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const { user, accessToken, effectiveRole, activeStudentId } = useAuth();
  const { language, t } = useTranslation();
  const dir = translations[language]?.meta?.dir || "ltr";

  const tFn = useCallback(
    (key, options) => {
      try {
        return t(key, options);
      } catch {
        return options?.defaultValue || key;
      }
    },
    [t],
  );

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [test, setTest] = useState(null);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const autosaveTimerRef = useRef(null);

  const getDraftKey = useCallback(() => {
    const mode = accessToken ? "authed" : "guest";
    return `casaclass:testAnswers:placement:${mode}:${String(testId)}`;
  }, [accessToken, testId]);

  const query = useMemo(
    () => new URLSearchParams(location.search || ""),
    [location.search],
  );
  const programSlug = String(query.get("program") || "").trim();
  const lead = useMemo(
    () => (programSlug ? loadPlacementLead(programSlug) : null),
    [programSlug],
  );

  useEffect(() => {
    if (programSlug) return;
    navigate("/placement-test", { replace: true });
  }, [navigate, programSlug]);

  const allowLeaveRef = useRef(false);

  const questionCount = useMemo(
    () =>
      asArray(test?.exercises).filter(
        (e) => String(e?.exercise_type || "") !== "stimulus",
      ).length,
    [test?.exercises],
  );
  const answeredCount = useMemo(
    () =>
      asArray(test?.exercises)
        .filter((e) => String(e?.exercise_type || "") !== "stimulus")
        .filter((e) => exerciseHasAnswer(e, answers[String(e.id)])).length,
    [answers, test?.exercises],
  );

  useEffect(() => {
    if (loading) return;
    if (accessToken) return;
    if (!programSlug) return;
    if (lead) return;
    const next = `/placement-test/start?program=${encodeURIComponent(programSlug)}&testId=${encodeURIComponent(String(testId))}`;
    navigate(next, { replace: true });
  }, [accessToken, lead, loading, navigate, programSlug, testId]);

  useEffect(() => {
    if (submitted) return undefined;
    if (allowLeaveRef.current) return undefined;

    const message = tFn("testsModule.take.doNotLeave", {
      defaultValue: "Do not leave the page during the test.",
    });

    const onBeforeUnload = (e) => {
      if (allowLeaveRef.current) return;
      e.preventDefault();
      e.returnValue = message;
      return message;
    };

    const onPopState = () => {
      if (allowLeaveRef.current) return;
      const ok = window.confirm(
        tFn("testsModule.take.confirmLeave", {
          defaultValue: "Do you want to leave the test?",
        }),
      );
      if (!ok) {
        window.history.pushState(null, "", window.location.href);
      }
    };

    window.history.pushState(null, "", window.location.href);
    window.addEventListener("beforeunload", onBeforeUnload);
    window.addEventListener("popstate", onPopState);
    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
      window.removeEventListener("popstate", onPopState);
    };
  }, [submitted, tFn]);

  const durationSeconds = useMemo(() => {
    const m = Number(test?.duration_minutes);
    if (!m || Number.isNaN(m)) return 0;
    return Math.max(0, Math.floor(m * 60));
  }, [test?.duration_minutes]);

  const [nowTick, setNowTick] = useState(Date.now());
  const [startedAtMs, setStartedAtMs] = useState(null);
  const autoSubmitAttemptedRef = useRef(false);

  useEffect(() => {
    let ignore = false;

    async function load() {
      setLoading(true);
      setError("");
      try {
        const payload = await programTestsService.fetchProgramTest({
          accessToken: user ? accessToken : undefined,
          testId,
        });
        if (ignore) return;
        setTest(payload);

        try {
          const raw = sessionStorage.getItem(getDraftKey());
          if (raw) {
            const parsed = JSON.parse(raw);
            if (parsed && typeof parsed === "object") setAnswers(parsed);
          }
        } catch {
          // ignore
        }
      } catch (e) {
        if (ignore) return;
        setError(
          e?.message ||
            tFn("testsModule.take.failedLoad", {
              defaultValue: "Failed to load test",
            }),
        );
      } finally {
        if (!ignore) setLoading(false);
      }
    }

    load();
    return () => {
      ignore = true;
    };
  }, [accessToken, getDraftKey, tFn, testId, user]);

  useEffect(() => {
    if (!testId) return undefined;
    if (submitted) return undefined;

    if (autosaveTimerRef.current) {
      window.clearTimeout(autosaveTimerRef.current);
      autosaveTimerRef.current = null;
    }

    autosaveTimerRef.current = window.setTimeout(() => {
      try {
        sessionStorage.setItem(getDraftKey(), JSON.stringify(answers || {}));
      } catch {
        // ignore
      }
    }, 450);

    return () => {
      if (autosaveTimerRef.current) {
        window.clearTimeout(autosaveTimerRef.current);
        autosaveTimerRef.current = null;
      }
    };
  }, [answers, getDraftKey, submitted, testId]);

  useEffect(() => {
    if (!durationSeconds || !testId) return undefined;
    autoSubmitAttemptedRef.current = false;

    let startMs = null;
    try {
      const raw = sessionStorage.getItem(getPlacementTimerKey(testId));
      startMs = raw ? Number(raw) : null;
    } catch {
      startMs = null;
    }

    if (!startMs || Number.isNaN(startMs)) {
      startMs = Date.now();
      try {
        sessionStorage.setItem(getPlacementTimerKey(testId), String(startMs));
      } catch {
        // ignore
      }
    }

    setStartedAtMs(startMs);
    const id = window.setInterval(() => setNowTick(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [durationSeconds, testId]);

  const exercises = useMemo(() => asArray(test?.exercises), [test?.exercises]);
  const questionExercises = useMemo(
    () =>
      exercises.filter((e) => String(e?.exercise_type || "") !== "stimulus"),
    [exercises],
  );

  const timeLeftSeconds = useMemo(() => {
    if (!durationSeconds || !startedAtMs) return null;
    const elapsed = Math.floor((nowTick - startedAtMs) / 1000);
    return Math.max(0, durationSeconds - elapsed);
  }, [durationSeconds, nowTick, startedAtMs]);

  const timeProgress = useMemo(() => {
    if (!durationSeconds || timeLeftSeconds === null) return 0;
    const done = durationSeconds - timeLeftSeconds;
    return Math.min(100, Math.max(0, (done / durationSeconds) * 100));
  }, [durationSeconds, timeLeftSeconds]);

  const canAuthedSubmit = Boolean(
    accessToken && (effectiveRole === "student" || effectiveRole === "parent"),
  );
  const canPublicSubmit = Boolean(!accessToken && lead);
  const canSubmit = Boolean(
    exercises.length && (canAuthedSubmit || canPublicSubmit),
  );

  // The "Send" button asks first when questions are still unanswered
  // (the automatic submit when time runs out does not).
  const confirmAndSubmit = () => {
    const missing = Math.max(0, questionCount - answeredCount);
    if (
      missing > 0 &&
      !window.confirm(
        tFn("testsModule.take.confirmUnanswered", {
          defaultValue:
            "You haven't answered {{n}} question(s). Send your answers anyway?",
          n: missing,
        }),
      )
    ) {
      return;
    }
    doSubmit();
  };

  const doSubmit = async () => {
    if (!exercises.length) return;

    if (accessToken && !canAuthedSubmit) {
      setError(
        tFn("testsModule.placement.requireInfoOnlyStudentsParents", {
          defaultValue: "Only students/parents can submit.",
        }),
      );
      return;
    }

    if (!lead && !accessToken) {
      setError(
        tFn("testsModule.placement.requireInfoFillFirst", {
          defaultValue: "Please fill the info form first.",
        }),
      );
      return;
    }

    setSubmitting(true);
    setError("");
    try {
      const answersPayload = exercises.map((ex) => ({
        exercise_id: ex.id,
        answer: answers[String(ex.id)] ?? {},
      }));

      const meta = {
        started_at: startedAtMs ? new Date(startedAtMs).toISOString() : null,
        ended_at: new Date().toISOString(),
        time_spent_seconds: startedAtMs
          ? Math.max(0, Math.floor((Date.now() - startedAtMs) / 1000))
          : null,
        time_left_seconds: timeLeftSeconds,
      };

      if (accessToken) {
        const payload = { answers: answersPayload, meta };
        if (user?.role === "parent" && activeStudentId) {
          payload.student_id = activeStudentId;
        }
        await programTestsService.submitProgramTest({
          accessToken,
          testId,
          payload,
        });
      } else {
        await programTestsService.submitProgramTestPublic({
          testId,
          payload: {
            lead,
            answers: answersPayload,
            meta,
          },
        });
      }

      try {
        sessionStorage.removeItem(getDraftKey());
      } catch {
        // ignore
      }

      clearPlacementLead(programSlug);
      setSubmitted(true);

      try {
        sessionStorage.removeItem(getPlacementTimerKey(testId));
      } catch {
        // ignore
      }
    } catch (e) {
      setError(
        e?.message ||
          tFn("testsModule.take.failedSubmit", {
            defaultValue: "Failed to submit test",
          }),
      );
    } finally {
      setSubmitting(false);
    }
  };

  const leaveTest = () => {
    if (
      !window.confirm(
        tFn("testsModule.take.confirmLeave", {
          defaultValue: "Do you want to leave the test?",
        }),
      )
    )
      return;

    allowLeaveRef.current = true;
    clearPlacementLead(programSlug);
    try {
      sessionStorage.removeItem(getPlacementTimerKey(testId));
    } catch {
      // ignore
    }
    try {
      sessionStorage.removeItem(getDraftKey());
    } catch {
      // ignore
    }
    navigate(`/placement-test?program=${encodeURIComponent(programSlug)}`);
  };

  useEffect(() => {
    if (!durationSeconds) return;
    if (timeLeftSeconds === null) return;
    if (timeLeftSeconds > 0) return;
    if (submitted || submitting) return;
    if (!canSubmit) return;
    if (autoSubmitAttemptedRef.current) return;
    autoSubmitAttemptedRef.current = true;
    doSubmit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [durationSeconds, timeLeftSeconds, submitted, submitting, canSubmit]);

  return (
    <main className="placement-take" dir={dir}>
      <div className="container placement-take__container">
        <header className="placement-take__header">
          <div>
            <h1 className="placement-take__title">
              {test?.title ||
                tFn("testsModule.take.test", { defaultValue: "Test" })}
            </h1>
            <div className="placement-take__meta">
              {test?.level_grade ? (
                <span>
                  {tFn("testsModule.common.level", { defaultValue: "Level" })}:{" "}
                  {test.level_grade}
                </span>
              ) : null}
              {test?.duration_minutes ? (
                <span>
                  {tFn("testsModule.take.durationLabel", {
                    defaultValue: "Duration",
                  })}
                  : {test.duration_minutes}{" "}
                  {tFn("testsModule.take.minutesShort", {
                    defaultValue: "min",
                  })}
                </span>
              ) : null}
              {questionCount ? (
                <span>
                  {tFn("testsModule.take.answered", {
                    defaultValue: "Answered:",
                  })}{" "}
                  {answeredCount}/{questionCount}
                </span>
              ) : null}
            </div>
          </div>
        </header>

        {durationSeconds ? (
          <section className="placement-take__timer">
            <div className="placement-take__timerRow">
              <div className="placement-take__timerLabel">
                {tFn("testsModule.take.timeLeft", {
                  defaultValue: "Time left",
                })}
              </div>
              <div className="placement-take__timerValue">
                {timeLeftSeconds === null
                  ? "--:--"
                  : formatClock(timeLeftSeconds)}
              </div>
            </div>
            <div className="placement-take__timerBar">
              <div
                className="placement-take__timerBarFill"
                style={{ width: `${timeProgress}%` }}
              />
            </div>
          </section>
        ) : null}

        {error ? <div className="placement-take__error">{error}</div> : null}
        {loading ? (
          <div className="placement-take__loading">
            {tFn("testsModule.common.loading", { defaultValue: "Loading..." })}
          </div>
        ) : null}

        {!loading && !test ? (
          <section className="placement-take__card">
            <h3>
              {tFn("testsModule.placement.testNotFound", {
                defaultValue: "Test not found",
              })}
            </h3>
            <p>
              {tFn("testsModule.placement.tryAnotherLevel", {
                defaultValue: "Try choosing another level.",
              })}
            </p>
            <Link
              className="btn primary"
              to={`/placement-test?program=${encodeURIComponent(programSlug)}`}
            >
              {tFn("testsModule.placement.chooseLevelCta", {
                defaultValue: "Choose level",
              })}
            </Link>
          </section>
        ) : null}

        {!loading && test ? (
          <>
            {submitted ? (
              <section className="placement-take__card">
                <h3>
                  {tFn("testsModule.take.submittedTitle", {
                    defaultValue: "Submitted",
                  })}
                </h3>
                <p>
                  {tFn("testsModule.take.submittedBody", {
                    defaultValue: "Your answers were sent to the teacher.",
                  })}
                </p>
                <div className="placement-take__submit">
                  <Link
                    className="btn primary"
                    to={`/placement-test?program=${encodeURIComponent(programSlug)}`}
                  >
                    {tFn("testsModule.placement.chooseLevelCta", {
                      defaultValue: "Choose level",
                    })}
                  </Link>
                </div>
              </section>
            ) : (
              <>
                {questionExercises.length ? (
                  <div
                    className="placement-take__nav"
                    role="navigation"
                    aria-label={tFn("testsModule.take.questionNavigation", {
                      defaultValue: "Question navigation",
                    })}
                  >
                    {questionExercises.map((ex, idx) => {
                      const qNum = idx + 1;
                      const answered = exerciseHasAnswer(
                        ex,
                        answers[String(ex.id)],
                      );
                      return (
                        <button
                          key={ex.id}
                          type="button"
                          className={
                            answered
                              ? "btn ghost placement-take__navBtn placement-take__navBtn--done"
                              : "btn ghost placement-take__navBtn"
                          }
                          onClick={() => {
                            const el = document.getElementById(`q-${ex.id}`);
                            if (el)
                              el.scrollIntoView({
                                behavior: "smooth",
                                block: "start",
                              });
                          }}
                          aria-label={tFn("testsModule.take.questionLabel", {
                            num: qNum,
                            defaultValue: `Question ${qNum}`,
                          })}
                        >
                          {qNum}
                        </button>
                      );
                    })}
                  </div>
                ) : null}

                {exercises.map((exercise) => (
                  <section
                    key={exercise.id}
                    className="placement-take__card"
                    id={`q-${exercise.id}`}
                  >
                    {/* <div className="placement-take__qHeader">
                      <div className="placement-take__qTitle">
                        <span className="placement-take__qNumber">
                          {String(exercise.exercise_type || "") === "stimulus"
                            ? "—"
                            : questionNumberById[String(exercise.id)]}
                        </span>
                        <span className="placement-take__qType">
                          {getTypeLabel(exercise.exercise_type)}
                        </span>
                      </div>
                      <div className="placement-take__qRight" />
                    </div> */}

                    {exercise.prompt ? (
                      <p className="placement-take__prompt">
                        {exercise.prompt}
                      </p>
                    ) : null}

                    <SharedExerciseRunner
                      prefix="placement-take"
                      exercise={exercise}
                      value={answers[String(exercise.id)]}
                      onChange={(v) =>
                        setAnswers((prev) => ({
                          ...prev,
                          [String(exercise.id)]: v,
                        }))
                      }
                      language={language}
                    />
                  </section>
                ))}

                {!exercises.length ? (
                  <section className="placement-take__card">
                    <div className="placement-take__empty">
                      {tFn("testsModule.take.noExercisesYet", {
                        defaultValue: "No exercises yet.",
                      })}
                    </div>
                  </section>
                ) : null}

                {accessToken && !canAuthedSubmit ? (
                  <div className="placement-take__note">
                    {tFn(
                      "testsModule.placement.requireInfoOnlyStudentsParents",
                      { defaultValue: "Only students/parents can submit." },
                    )}
                  </div>
                ) : null}

                <div className="placement-take__submit">
                  <button
                    className="btn primary"
                    type="button"
                    onClick={confirmAndSubmit}
                    disabled={!canSubmit || submitting}
                  >
                    {submitting
                      ? tFn("testsModule.common.submitting", {
                          defaultValue: "Submitting...",
                        })
                      : tFn("testsModule.common.submit", {
                          defaultValue: "Submit",
                        })}
                  </button>
                  <button
                    className="btn ghost"
                    type="button"
                    onClick={leaveTest}
                    disabled={submitting}
                  >
                    {tFn("testsModule.common.leave", { defaultValue: "Leave" })}
                  </button>
                </div>
              </>
            )}
          </>
        ) : null}
      </div>
    </main>
  );
}
